import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import locale from '@glu/locale';
import { Button, SMALL } from '@glu/buttons-react';
import { withStyles, withTheme } from '@glu/theming';
import SchedulerContext from '../SchedulerContext';
import { types } from './Recurrence.reducer';
import EveryN, { SELECT } from './EveryN';
import styles from './Weekly.styles';
import TimePicker from './TimePicker';
import { getWeeklyOptions } from './Weekly.options';
import '../themeDefaults';
import {
  SUN, MON, TUE, WED, THU, FRI, SAT
} from '../utils/recurrence';

const Weekly = ({
  classes, onChange, nWeeks, daysInWeek, dayOptions, defaultValue, allowWeekend,
  atTime, canSetTime, minTime, maxTime, hideMinutes, maxDays, everyN
}) => {
  const {
    htmlId, disabled
  } = useContext(SchedulerContext);
  const daysSelected = daysInWeek || defaultValue;
  /** checks if day should be disabled based on maxDays, given day, and disabled props */
  const isDayDisabled = (day) => (
    maxDays !== 1
    && (
      disabled
      || (
        maxDays
        && maxDays === daysSelected?.length && !daysSelected.includes(day.value))
    )
  );

  const generateDayResponse = (day) => (
    maxDays === 1
      ? {
        type: types.SET_WEEK,
        response: daysSelected?.includes(day.value) ? [] : [day.value]
      }
      : {
        type: daysSelected?.includes(day.value)
          ? types.REMOVE_DAY_WEEK
          : types.ADD_DAY_WEEK,
        response: day.value
      }
  );

  useEffect(() => {
    onChange({
      type: types.SET_WEEK,
      response: daysSelected
    });
  }, []);

  return (
    <div data-qa="weekly" className={`${classes.weekly} `}>
      {everyN && (
        <EveryN
          everyText={locale.get('schedulerReact.everyXWeeks').split('{0}')}
          min={1}
          max={4}
          value={nWeeks}
          type={SELECT}
          onChange={(event) => onChange({
            type: types.WEEKLY,
            response: event.target.value
          })}
        />
      )}
      <div className={classes.buttons}>
        {getWeeklyOptions(locale, allowWeekend
          ? [...dayOptions, SUN, SAT]
          : dayOptions).map(day => (
            <Button
              key={day.value}
              id={`${htmlId}-${day.label}`}
              data-qa={`weekly-button-${day.label}`}
              data-active={daysSelected && daysSelected.includes(day.value)}
              className={`
                ${classes.button} 
                ${daysSelected && daysSelected.includes(day.value) && classes.active}
            `}
              disabled={isDayDisabled(day)}
              size={SMALL}
              type="button"
              onClick={() => onChange(generateDayResponse(day))}
            >
              {day.label}
            </Button>
        ))}
      </div>
      {canSetTime && (
        <TimePicker
          htmlId="weekly-timePicker"
          onChange={(time) => onChange({
            type: types.AT_TIME,
            response: time.value
          })}
          minTime={minTime}
          maxTime={maxTime}
          value={atTime}
          hideMinutes={hideMinutes}
        />
      )}
    </div>
  );
};

Weekly.propTypes = {
  /** Classes provided by the theme */
  classes: PropTypes.objectOf(PropTypes.string).isRequired,

  /** Value of every x weeks */
  nWeeks: PropTypes.number,

  /** Allows for setting a time of day */
  canSetTime: PropTypes.bool,

  /** Hides minute controls, only displaying hours to control * */
  hideMinutes: PropTypes.bool,

  /** Allow weekends */
  allowWeekend: PropTypes.bool,

  /** String containing at time value */
  atTime: PropTypes.string,

  /** Controls whether to display every n control */
  everyN: PropTypes.bool,

  /** Array containing values of days in week */
  daysInWeek: PropTypes.instanceOf(Array),

  /** Days that are available to select */
  dayOptions: PropTypes.arrayOf(PropTypes.oneOf([SUN, MON, TUE, WED, THU, FRI, SAT])),

  /** Minimum time value of input */
  minTime: PropTypes.string,

  /** Maximum time value of input */
  maxTime: PropTypes.string,

  /** Maximum number of days to select */
  maxDays: PropTypes.number,

  /** Default selected days if no value was set */
  defaultValue: PropTypes.arrayOf(PropTypes.oneOf([SUN, MON, TUE, WED, THU, FRI, SAT])),

  /** Callback to set recurrence value */
  onChange: PropTypes.func.isRequired
};

Weekly.defaultProps = {
  nWeeks: 1,
  daysInWeek: undefined,
  everyN: true,
  canSetTime: true,
  allowWeekend: null,
  dayOptions: [
    MON, TUE, WED, THU, FRI
  ],
  defaultValue: undefined,
  atTime: undefined,
  minTime: undefined,
  maxTime: undefined,
  maxDays: undefined,
  hideMinutes: false
};

export const BareWeekly = Weekly;
export default withTheme(withStyles(styles)(Weekly));
